import React, { useMemo, useLayoutEffect, useRef } from 'react';
import './TimelineView.css';
import { 
    toDateKey, isSameDay
} from '../../../../../utils/dateUtils';
import { TimelineHeader } from './TimelineHeader';
import { TimelineAllDaySection } from './TimelineAllDaySection';
import { TimelineDayRow } from './TimelineDayRow';

/**
 * TimelineGrid Component
 * 
 * The main orchestrator for the horizontal timeline view.
 * Coordinates the sticky header, the global all-day section, and individual day rows.
 * Handles the high-level layout and scroll synchronization for the grid.
 * 
 * @param {Object} props
 * @param {React.RefObject} props.containerRef - Ref for the outer modal container
 * @param {React.RefObject} props.scrollRef - Ref for the scrollable viewport
 * @param {number} props.scrollX - Current horizontal scroll position
 * @param {Date[]} props.days - List of dates to display
 * @param {number} props.hourStart - Starting hour of the grid
 * @param {number} props.hourEnd - Ending hour of the grid
 * @param {number} props.dynamicHourWidth - Pixel width per hour slot
 * @param {number} props.actualAlldayWidth - Width of the sticky all-day column
 * @param {number} props.GRID_BUFFER - Offset for the first hour label
 * @param {number} props.TIMELINE_WIDTH - Total calculated width of the time grid
 * @param {number} props.dynamicRowHeight - Default height for a day row
 * @param {boolean} props.isAllDayCollapsed - Whether the all-day section is hidden
 * @param {Function} props.setIsAllDayCollapsed - Toggle for all-day section
 * @param {boolean} props.isReadOnly - Whether interactions are disabled
 * @param {Object} props.timedByKey - Timed assignments grouped by date key
 * @param {Object} props.alldayAssignments - List of all-day assignments
 * @param {Object} props.interaction - Current drag/resize state
 * @returns {JSX.Element}
 */
export function TimelineGrid({
    containerRef, scrollRef, scrollX, 
    days, 
    hourStart, hourEnd, dynamicHourWidth, actualAlldayWidth, GRID_BUFFER, TIMELINE_WIDTH, dynamicRowHeight,
    isAllDayCollapsed, setIsAllDayCollapsed,
    hoveringAlldayResizer, setHoveringAlldayResizer,
    resizingAllday, setResizingAllday,
    setHasManuallyResized, alldayWidth, setAlldayWidth,
    allDayLayouts, timedByKey,
    interaction, setInteraction, interactionEnded,
    handleInteractionStart, getTimedLayout, handleDrop, handleDropAllDay, handleDragOver, handleDragLeave,
    onDayClick, handleContextMenu, onAssignmentClick, assignmentConflictsMap,
    onAssignmentHover,
    onAssignmentHoverEnd,
    onConflictHover,
    onConflictHoverEnd,
    today, nowHour, nowLeft, isConflictDetectionEnabled = true,
    isReadOnly = false
}) {
    // --- LAYOUT CONSTANTS ---
    const MIN_CHIP_HEIGHT = 22;
    const LANE_GAP = 6;
    const VERT_PADDING = 14; 

    // --- PRE-CALCULATE DYNAMIC HEIGHTS FOR ALL ROWS ---
    const { dayHeights, dayOffsets, totalActualHeight } = useMemo(() => {
        let currentOffset = 0;
        const heights = [];
        const offsets = [];
        
        days.forEach(day => {
            const key = toDateKey(day);
            const dayTimedEvents = timedByKey[key] || [];
            const layouts = getTimedLayout(dayTimedEvents, interaction);
            const dayMaxLanes = layouts.length > 0 
                ? Math.max(...layouts.map(l => l.totalCols || 1)) 
                : 0;
            
            const requiredHeight = (dayMaxLanes * (MIN_CHIP_HEIGHT + LANE_GAP)) + VERT_PADDING;
            const actualH = Math.max(dynamicRowHeight, requiredHeight);
            
            heights.push(actualH);
            offsets.push(currentOffset);
            currentOffset += actualH;
        });
        
        return { dayHeights: heights, dayOffsets: offsets, totalActualHeight: currentOffset };
    }, [days, timedByKey, dynamicRowHeight, getTimedLayout, interaction, MIN_CHIP_HEIGHT, LANE_GAP, VERT_PADDING]);
    
    // Scroll persistence logic removed for stability

    return (
        <div className="calendar-timeline-wrapper horizontal" ref={containerRef}>
            <div className="timeline-scroll-body" ref={scrollRef} style={{ position: 'relative', minHeight: 0 }}>
                {/* 1. Header (Sticky) */}
                <TimelineHeader 
                    actualAlldayWidth={actualAlldayWidth}
                    isAllDayCollapsed={isAllDayCollapsed}
                    setIsAllDayCollapsed={setIsAllDayCollapsed}
                    TIMELINE_WIDTH={TIMELINE_WIDTH}
                    hourStart={hourStart}
                    hourEnd={hourEnd}
                    GRID_BUFFER={GRID_BUFFER}
                    dynamicHourWidth={dynamicHourWidth}
                />

                {/* 2. Global All-Day Section (Resizer & Layer) */}
                <TimelineAllDaySection 
                    isAllDayCollapsed={isAllDayCollapsed}
                    isReadOnly={isReadOnly}
                    setHoveringAlldayResizer={setHoveringAlldayResizer}
                    setResizingAllday={setResizingAllday}
                    setHasManuallyResized={setHasManuallyResized}
                    alldayWidth={alldayWidth}
                    setAlldayWidth={setAlldayWidth}
                    actualAlldayWidth={actualAlldayWidth}
                    totalActualHeight={totalActualHeight}
                    resizingAllday={resizingAllday}
                    hoveringAlldayResizer={hoveringAlldayResizer}
                    allDayLayouts={allDayLayouts}
                    interaction={interaction}
                    setInteraction={setInteraction}
                    assignmentConflictsMap={assignmentConflictsMap}
                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                    handleDropAllDay={handleDropAllDay}
                    dayOffsets={dayOffsets}
                    dayHeights={dayHeights}
                    days={days}
                    onAssignmentHover={onAssignmentHover}
                    onAssignmentHoverEnd={onAssignmentHoverEnd}
                    onAssignmentClick={onAssignmentClick}
                    handleContextMenu={handleContextMenu}
                    handleInteractionStart={handleInteractionStart}
                    interactionEnded={interactionEnded}
                    onConflictHoverEnd={onConflictHoverEnd}
                />

                {/* 3. Individual Day Rows */}
                {days.map((day, dayIdx) => (
                    <TimelineDayRow 
                        key={toDateKey(day)}
                        day={day}
                        dayIdx={dayIdx}
                        dayHeights={dayHeights}
                        timedByKey={timedByKey}
                        interaction={interaction}
                        getTimedLayout={getTimedLayout}
                        isSameDay={isSameDay}
                        today={today}
                        actualAlldayWidth={actualAlldayWidth}
                        isReadOnly={isReadOnly}
                        handleDropAllDay={handleDropAllDay}
                        handleDragOver={handleDragOver}
                        handleDragLeave={handleDragLeave}
                        interactionEnded={interactionEnded}
                        onDayClick={onDayClick}
                        handleContextMenu={handleContextMenu}
                        TIMELINE_WIDTH={TIMELINE_WIDTH}
                        hourStart={hourStart}
                        hourEnd={hourEnd}
                        dynamicHourWidth={dynamicHourWidth}
                        GRID_BUFFER={GRID_BUFFER}
                        nowHour={nowHour}
                        nowLeft={nowLeft}
                        assignmentConflictsMap={assignmentConflictsMap}
                        isConflictDetectionEnabled={isConflictDetectionEnabled}
                        handleDrop={handleDrop}
                        onAssignmentHover={onAssignmentHover}
                        onAssignmentHoverEnd={onAssignmentHoverEnd}
                        onConflictHover={onConflictHover}
                        onConflictHoverEnd={onConflictHoverEnd}
                        onAssignmentClick={onAssignmentClick}
                        handleInteractionStart={handleInteractionStart}
                        scrollX={scrollX}
                        setInteraction={setInteraction}
                    />
                ))}
            </div>
            <div className="timeline-footer"></div>
        </div>
    );
}
